import React from 'react';
import { PapiDeviation, PapiGallection } from '@wix/da-types-papi';
import { GridElement, StandardGrid } from '@wix/da-shared-react/pkg/Grid';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';
import { ShopItemType } from '../../../../../types/shop';
import ShopThumb, {
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import ShopFolderThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/ShopFolder';
import CommissionThumbWithMenu from '../_partials/CommissionThumbWithMenu';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadableDuperbrowseContext from '../../../../contexts/LoadableDuperbrowseContext';
import EmptyState from '../_partials/EmptyState';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';

const CARD_PANEL_HEIGHT = 89;

export const itemTypeToThumbTypeMap = {
  [ShopItemType.PREMIUM_CONTENT]: ShopThumbType.PCP,
  [ShopItemType.COMMISSIONS]: ShopThumbType.COMMISSION,
  [ShopItemType.PRINTS]: ShopThumbType.PRINT,
};

const itemTypeToBiWidgetMap = {
  [ShopItemType.PREMIUM_CONTENT]: 'downloads',
  [ShopItemType.COMMISSIONS]: 'commissions',
  [ShopItemType.PRINTS]: 'prints',
};

export interface Props {
  streamId: string;
  className?: string;
  items: PapiGallection[] | PapiDeviation[];
  itemType: ShopItemType;
  hasMore: boolean;
  isFetching: boolean;
  fetchMore: () => void;
  duperbrowseTitle: string;
  emptyState?: React.ReactNode;
}

export class ShopGrid extends React.Component<Props> {
  render() {
    const {
      streamId,
      items,
      itemType,
      hasMore,
      isFetching,
      fetchMore,
      emptyState,
      duperbrowseTitle,
    } = this.props;

    if (!items.length) {
      return emptyState === false
        ? null
        : emptyState || <EmptyState view="large" shopItemType={itemType} />;
    }

    return (
      <ErrorBoundary>
        <LoadableDuperbrowseContext
          streamId={streamId}
          parent={{ title: duperbrowseTitle }}
        >
          <BiLoggerContextProvider
            value={{ widgetname: itemTypeToBiWidgetMap[itemType] }}
          >
            <StandardGrid
              elementCount={items.length}
              cookieType={MeasuredCookieType.USER_PROFILE_SHOP}
              getElementId={this.getItemId}
              adjustHeight={CARD_PANEL_HEIGHT}
              enableScrollOptim
            >
              {this.renderElement}
            </StandardGrid>
          </BiLoggerContextProvider>
        </LoadableDuperbrowseContext>
        {isFetching && <LoadingIndicator />}
        <OnScrollBottom
          distanceToBottom={2}
          distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
          onScrolledToBottom={hasMore && !isFetching ? fetchMore : undefined}
        />
      </ErrorBoundary>
    );
  }

  renderElement = (element: GridElement) => {
    const { items, itemType } = this.props;
    const { index, width, height, style } = element;

    let thumb;

    switch (itemType) {
      case ShopItemType.COMMISSIONS:
        thumb = (
          <CommissionThumbWithMenu
            key={index}
            deviation={items[index] as PapiDeviation}
            width={width}
            height={height}
          />
        );
        break;
      case ShopItemType.PREMIUM_FOLDERS:
        thumb = (
          <ShopFolderThumb
            key={index}
            folder={items[index] as PapiGallection}
            width={width}
            height={height}
          />
        );
        break;
      default:
        thumb = (
          <ShopThumb
            key={index}
            deviation={items[index] as PapiDeviation}
            width={width}
            height={height}
            type={itemTypeToThumbTypeMap[itemType]}
            showAuthor={false}
          />
        );
    }

    return <div style={{ ...style }}>{thumb}</div>;
  };

  getItemId = (index: number) => {
    const { items = [], itemType } = this.props;
    if (itemType === ShopItemType.PREMIUM_FOLDERS) {
      const item = items[index] as PapiGallection;
      return `${index}-${item.folderId}`;
    } else {
      const item = items[index] as PapiDeviation;
      return `${index}-${item.deviationId}`;
    }
  };
}

export default ShopGrid;
